<template>
  <div class="min-h-screen bg-gray-50 flex flex-col items-center py-12 px-4">
    <div v-if="booking">
      <!-- Hero -->
      <div class="bg-white shadow-md rounded-lg w-full max-w-2xl p-8 text-center">
        <h1 class="text-2xl font-extrabold mb-4 text-green-600">🎉 Your consultation is confirmed!</h1>
        <p class="text-gray-700 mb-6">We look forward to speaking with you.</p>

        <!-- Booking details -->
        <div class="bg-gray-100 rounded-lg p-4 mb-6">
          <p class="font-semibold">Date & Time</p>
          <p>{{ formattedDate }} &middot; {{ formattedTime }} ({{ bookingTimeZone }})</p>
          <p class="mt-2 font-semibold">Location</p>
          <a
            :href="booking.videoCallUrl"
            target="_blank"
            class="text-blue-600 hover:underline"
          >
            Join via Google Meet
          </a>
        </div>

        <!-- Primary CTAs -->
        <div class="flex flex-col sm:flex-row justify-center gap-4 mb-8">
          <a
            :href="googleCalendarLink"
            target="_blank"
            class="btn btn-outline btn-sm"
          >
            Add to Google Calendar
          </a>
          <button
            @click="downloadICS"
            class="btn btn-outline btn-sm"
          >
            Download .ics for Outlook/iCal
          </button>
          <router-link
            to="/prep-guide"
            class="btn btn-primary btn-sm"
          >
            Get Prepared
          </router-link>
          <a
            :href="inviteLink"
            class="btn btn-secondary btn-sm"
          >
            Invite a Colleague
          </a>
        </div>

        <!-- Secondary Engagement -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Quick survey -->
          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-lg font-bold mb-4">Help us tailor the call</h2>
            <form @submit.prevent="submitSurvey" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">What's your #1 priority?</label>
                <select v-model="survey.priority" class="select select-bordered w-full">
                  <option disabled value="">Select one</option>
                  <option>Increase sales</option>
                  <option>Improve retention</option>
                  <option>Optimize operations</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Additional notes</label>
                <textarea
                  v-model="survey.notes"
                  rows="3"
                  class="textarea textarea-bordered w-full"
                  placeholder="Anything else we should know?"
                ></textarea>
              </div>
              <button type="submit" class="btn btn-primary btn-block btn-sm">Submit</button>
            </form>
          </div>

          <!-- PDF download + Upsell -->
          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-lg font-bold mb-4">Download our free guide</h2>
            <a
              href="/assets/guides/top-5-mistakes.pdf"
              target="_blank"
              class="btn btn-outline btn-block btn-sm mb-6"
            >
              Top 5 Mistakes to Avoid (PDF)
            </a>
            <h2 class="text-lg font-bold mb-4">Upgrade to Total+</h2>
            <p class="text-gray-600 mb-4">Get our 30-day action plan & 1:1 support.</p>
            <router-link
              to="/pricing#total-plus"
              class="btn btn-primary btn-block btn-sm"
            >
              Learn More
            </router-link>
          </div>
        </div>

        <!-- Continue browsing -->
        <div class="mt-12 text-center">
          <router-link to="/case-studies" class="text-sm text-blue-600 hover:underline">
            See our latest case studies &rarr;
          </router-link>
        </div>
      </div>
    </div>
    <div v-else class="bg-white shadow-md rounded-lg w-full max-w-2xl p-8 text-center">
      <h2 class="text-xl font-bold mb-4">No Consultation Found</h2>
      <p class="text-gray-600 mb-6">It looks like you accessed this page directly. To view confirmation details, please book a consultation first or <router-link to="/">return home</router-link>.</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

interface Booking {
  startTime: string;
  endTime: string;
  timeZone: string;
  videoCallUrl: string;
  title: string;
  description: string;
}

const route = useRoute();
const booking = ref<Booking | null>(null);
onMounted(() => {
  let raw = sessionStorage.getItem('bookingData');
  if (!raw && route.query.booking) {
    raw = Array.isArray(route.query.booking) ? route.query.booking[0] : route.query.booking as string;
  }
  if (raw) {
    try {
      booking.value = JSON.parse(raw);
      sessionStorage.removeItem('bookingData');
    } catch {
      booking.value = null;
    }
  }
});

const formattedDate = computed(() => {
  if (!booking.value) return '';
  const dt = new Date(booking.value.startTime);
  return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
});
const formattedTime = computed(() => {
  if (!booking.value) return '';
  const dt = new Date(booking.value.startTime);
  return dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
});
const bookingTimeZone = computed(() => booking.value?.timeZone || '');

const googleCalendarLink = computed(() => {
  if (!booking.value) return '#';
  const start = new Date(booking.value.startTime).toISOString().replace(/-|:|\.\d{3}/g, '');
  const end = new Date(booking.value.endTime).toISOString().replace(/-|:|\.\d{3}/g, '');
  const text = encodeURIComponent(booking.value.title);
  const details = encodeURIComponent(booking.value.description || '');
  const location = encodeURIComponent(booking.value.videoCallUrl);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${start}/${end}&details=${details}&location=${location}`;
});

function downloadICS() {
  if (!booking.value) return;
  const dtStart = new Date(booking.value.startTime).toISOString().replace(/-|:|\.\d{3}/g, '');
  const dtEnd = new Date(booking.value.endTime).toISOString().replace(/-|:|\.\d{3}/g, '');
  const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nUID:${crypto.randomUUID()}\nDTSTAMP:${dtStart}\nDTSTART:${dtStart}\nDTEND:${dtEnd}\nSUMMARY:${booking.value.title}\nDESCRIPTION:${booking.value.description}\nLOCATION:${booking.value.videoCallUrl}\nEND:VEVENT\nEND:VCALENDAR`;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'consultation.ics';
  a.click();
  URL.revokeObjectURL(url);
}

const inviteLink = computed(() => {
  if (!booking.value) return '#';
  const subject = encodeURIComponent(`Join me for a consultation: ${booking.value.title}`);
  const body = encodeURIComponent(
    `Hi,\n\nI'd like to invite you to join my consultation on ${formattedDate.value} at ${formattedTime.value}.\nJoin here: ${booking.value.videoCallUrl}\n\nSee you there!`
  );
  return `mailto:?subject=${subject}&body=${body}`;
});

const survey = ref({ priority: '', notes: '' });
function submitSurvey() {
  if (!booking.value) return;
  fetch('/api/consultation-survey', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...survey.value, bookingId: booking.value.title }),
  });
  alert('Thanks for your feedback!');
}
</script>

<style scoped>
/* additional scoped styles if needed */
</style>
